import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { config } from "../services/runtimeConfig";

const TICK_MS = 1000;
const WARNING_THRESHOLD_MS = 2 * 60_000; // show "extend session" banner at 2 minutes left

/**
 * Hard 15-minute session cap (config.sessionTimeoutMinutes), reset on any
 * tracked user activity (click/keydown), auto sign-out at zero.
 *
 * This is a UX-layer mirror of a real server-side cap: api/security/
 * session_manager.py's _session_timeout() has an "admin" branch
 * (SESSION_TIMEOUT_ADMIN_MINUTES, default 15) that never honors "stay
 * logged in", so a replayed admin bearer token can't outlive this UI.
 * Keep config.sessionTimeoutMinutes in sync with SESSION_TIMEOUT_ADMIN_MINUTES
 * if either changes.
 */
export function useAdminSession() {
  const { logout, isAuthenticated } = useAuth();
  const [remainingMs, setRemainingMs] = useState(config.sessionTimeoutMinutes * 60_000);
  const deadlineRef = useRef(Date.now() + config.sessionTimeoutMinutes * 60_000);

  const resetTimer = useCallback(() => {
    deadlineRef.current = Date.now() + config.sessionTimeoutMinutes * 60_000;
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const activityEvents = ["click", "keydown", "scroll"];
    activityEvents.forEach((evt) => window.addEventListener(evt, resetTimer));

    const interval = setInterval(() => {
      const remaining = Math.max(0, deadlineRef.current - Date.now());
      setRemainingMs(remaining);
      if (remaining <= 0) {
        logout();
      }
    }, TICK_MS);

    return () => {
      activityEvents.forEach((evt) => window.removeEventListener(evt, resetTimer));
      clearInterval(interval);
    };
  }, [isAuthenticated, logout, resetTimer]);

  const minutes = Math.floor(remainingMs / 60_000);
  const seconds = Math.floor((remainingMs % 60_000) / 1000);

  return {
    remainingMs,
    label: `${minutes}:${String(seconds).padStart(2, "0")}`,
    showWarning: remainingMs > 0 && remainingMs <= WARNING_THRESHOLD_MS,
    extendSession: resetTimer,
  };
}
